import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  PhoneFilled,
  AudioMutedOutlined,
  AudioOutlined,
  PauseOutlined,
  CaretRightOutlined,
  SoundOutlined,
  SwapOutlined,
  UsergroupAddOutlined,
  AppstoreOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  MinusOutlined,
  CoffeeOutlined,
  FileTextOutlined,
  UserOutlined,
  HolderOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  PoweroffOutlined,
  CheckCircleFilled,
  ArrowUpOutlined,
  ArrowDownOutlined,
  StopOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import "./CallWidget.scss";

/*
 * Floating, draggable call widget - UI only.
 * The launcher can be dragged anywhere on the page; a tap (no drag) opens a
 * phone-sized panel holding every calling option. No dialer API is wired up
 * yet: all data below is placeholder and every action only changes local state.
 */

const FAB_SIZE = 56;
const PANEL_W = 350;
const PANEL_H = 660;
const EDGE = 12;
const DRAG_THRESHOLD = 5;
const STORAGE_KEY = "call_widget_fab_pos";
const MAX_NUMBER = 20;
const DIAL_CHAR = /^[0-9*#+]$/;
const cleanNumber = (v) => v.replace(/[^\d+*#]/g, "");

const KEYS = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];

const DIAL_MODES = ["Manual", "Preview", "Progressive"];

const AGENT_STATUSES = [
  { key: "ready", label: "Ready", tone: "green", icon: <CheckCircleFilled /> },
  { key: "tea", label: "Tea Break", tone: "amber", icon: <CoffeeOutlined /> },
  { key: "lunch", label: "Lunch Break", tone: "amber", icon: <CoffeeOutlined /> },
  { key: "meeting", label: "Meeting", tone: "blue", icon: <TeamOutlined /> },
  { key: "offline", label: "Offline", tone: "muted", icon: <PoweroffOutlined /> },
];

const RECENTS = [
  { name: "Rahul Sharma", number: "+91 98765 43210", dir: "out", time: "10:42 AM", dur: "04:12" },
  { name: "Priya Verma", number: "+91 91234 56780", dir: "in", time: "10:05 AM", dur: "01:48" },
  { name: "Amit Patel", number: "+91 99887 76655", dir: "missed", time: "09:31 AM", dur: "" },
  { name: "Sneha Iyer", number: "+91 90000 11122", dir: "out", time: "Yesterday", dur: "06:03" },
  { name: "Unknown", number: "+91 88776 65544", dir: "in", time: "Yesterday", dur: "00:37" },
];

const CALLBACKS = [
  { name: "Vikram Singh", number: "+91 97654 32109", loan: "LN-204587", at: "Today, 12:30 PM", due: true },
  { name: "Neha Gupta", number: "+91 93456 78123", loan: "LN-198334", at: "Today, 03:00 PM", due: false },
  { name: "Karan Mehta", number: "+91 94567 89012", loan: "LN-176902", at: "Tomorrow, 11:00 AM", due: false },
];

const TRANSFER_TARGETS = [
  { name: "Anjali (Team Lead)", ext: "201", online: true },
  { name: "Rohit (Supervisor)", ext: "105", online: true },
  { name: "Legal Desk", ext: "310", online: false },
  { name: "Escalation Queue", ext: "900", online: true },
];

const DISPOSITIONS = [
  "Promise To Pay",
  "Paid",
  "Refused To Pay",
  "Call Back",
  "Not Reachable",
  "Switched Off",
  "Wrong Number",
  "Dispute",
];

const readStoredPos = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max));

const clampFab = ({ x, y }) => ({
  x: clamp(x, EDGE, window.innerWidth - FAB_SIZE - EDGE),
  y: clamp(y, EDGE, window.innerHeight - FAB_SIZE - EDGE),
});

const formatTimer = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

/* Pointer-based dragging shared by the launcher and the panel header.
   Reports whether the gesture moved far enough to count as a drag, so the
   launcher can tell a tap from a drag. */
const useDrag = (onMove) => {
  const start = useRef(null);

  const onPointerDown = useCallback((e, origin) => {
    if (e.button !== 0) return;
    start.current = { px: e.clientX, py: e.clientY, ox: origin.x, oy: origin.y, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e) => {
      const s = start.current;
      if (!s) return;
      const dx = e.clientX - s.px;
      const dy = e.clientY - s.py;
      if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      s.moved = true;
      onMove({ x: s.ox + dx, y: s.oy + dy });
    },
    [onMove]
  );

  const onPointerUp = useCallback((e) => {
    const moved = start.current?.moved ?? false;
    start.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    return moved;
  }, []);

  return { onPointerDown, onPointerMove, onPointerUp };
};

const CallWidget = () => {
  const { user } = useSelector((state) => state.auth);
  const { pathname } = useLocation();
  const [fabPos, setFabPos] = useState(
    () =>
      clampFab(
        readStoredPos() || {
          x: window.innerWidth - FAB_SIZE - 24,
          y: window.innerHeight - FAB_SIZE - 24,
        }
      )
  );
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ x: 0, y: 0 });

  const [tab, setTab] = useState("keypad"); // keypad | recents | callbacks | status
  const [screen, setScreen] = useState("home"); // home | calling | incall | wrapup
  const [sheet, setSheet] = useState(null); // null | transfer | conference | keypad | notes

  const [number, setNumber] = useState("");
  const [dialMode, setDialMode] = useState("Manual");
  const [status, setStatus] = useState("ready");
  const [contact, setContact] = useState(null);

  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [speaker, setSpeaker] = useState(false);
  const [recording, setRecording] = useState(true);
  const [seconds, setSeconds] = useState(0);
  const [dtmf, setDtmf] = useState("");
  const [notes, setNotes] = useState("");
  const [transferTo, setTransferTo] = useState("");

  const [disposition, setDisposition] = useState("");
  const [remarks, setRemarks] = useState("");
  const [search, setSearch] = useState("");

  const numberRef = useRef(null);
  const [pressed, setPressed] = useState(null);
  const pressTimer = useRef(null);

  const panelSize = () => ({
    w: Math.min(PANEL_W, window.innerWidth - EDGE * 2),
    h: Math.min(PANEL_H, window.innerHeight - EDGE * 2),
  });

  const clampPanel = useCallback(({ x, y }) => {
    const { w, h } = panelSize();
    return {
      x: clamp(x, EDGE, window.innerWidth - w - EDGE),
      y: clamp(y, EDGE, window.innerHeight - h - EDGE),
    };
  }, []);

  // Keep things on screen when the window is resized.
  useEffect(() => {
    const onResize = () => {
      setFabPos((p) => clampFab(p));
      setPanelPos((p) => clampPanel(p));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampPanel]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(fabPos));
    } catch {
      /* position memory is a convenience only */
    }
  }, [fabPos]);

  // Simulated call progression + timer (UI only).
  useEffect(() => {
    if (screen !== "calling") return undefined;
    const t = setTimeout(() => setScreen("incall"), 1800);
    return () => clearTimeout(t);
  }, [screen]);

  useEffect(() => {
    if (screen !== "incall" || held) return undefined;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [screen, held]);

  const fabDrag = useDrag(useCallback((p) => setFabPos(clampFab(p)), []));
  const panelDrag = useDrag(useCallback((p) => setPanelPos(clampPanel(p)), [clampPanel]));

  const openPanel = () => {
    const { w, h } = panelSize();
    // Open beside the launcher, flipping to whichever side has room.
    const x = fabPos.x + FAB_SIZE / 2 > window.innerWidth / 2 ? fabPos.x + FAB_SIZE - w : fabPos.x;
    const y = fabPos.y + FAB_SIZE / 2 > window.innerHeight / 2 ? fabPos.y - h - 12 : fabPos.y + FAB_SIZE + 12;
    setPanelPos(clampPanel({ x, y }));
    setOpen(true);
  };

  const resetCallState = () => {
    setMuted(false);
    setHeld(false);
    setSpeaker(false);
    setRecording(true);
    setSeconds(0);
    setDtmf("");
    setSheet(null);
    setTransferTo("");
  };

  const startCall = (target) => {
    const c = target || { name: "Unknown", number: number || "" };
    if (!c.number) return;
    resetCallState();
    setNotes("");
    setContact(c);
    setScreen("calling");
  };

  const endCall = () => {
    setSheet(null);
    setScreen("wrapup");
  };

  const finishWrapup = () => {
    setDisposition("");
    setRemarks("");
    setContact(null);
    setNumber("");
    setScreen("home");
  };

  const currentStatus = AGENT_STATUSES.find((s) => s.key === status);
  const inCall = screen === "calling" || screen === "incall";
  const dialing = open && screen === "home" && tab === "keypad";
  const dtmfOpen = open && screen === "incall" && sheet === "keypad";

  // Light up the on-screen key that matches a physical key press.
  const flashKey = useCallback((k) => {
    setPressed(k);
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => setPressed(null), 140);
  }, []);

  useEffect(() => () => clearTimeout(pressTimer.current), []);

  const appendNumber = useCallback(
    (d) => setNumber((n) => cleanNumber(n + d).slice(0, MAX_NUMBER)),
    []
  );

  // Focus the number field whenever the keypad is showing, so typing just works.
  useEffect(() => {
    if (dialing) numberRef.current?.focus({ preventScroll: true });
  }, [dialing]);

  /* System keyboard support. Keys typed while the keypad (or the in-call DTMF
     keypad) is showing go to it, as long as focus isn't in some other field -
     the number input itself handles its own typing natively. */
  useEffect(() => {
    if (!dialing && !dtmfOpen) return undefined;

    const isOtherField = (el) =>
      el &&
      el !== numberRef.current &&
      (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);

    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isOtherField(document.activeElement)) return;
      const inNumberField = document.activeElement === numberRef.current;

      if (DIAL_CHAR.test(e.key)) {
        flashKey(e.key === "+" ? "0" : e.key);
        if (inNumberField) return; // native input inserts it
        e.preventDefault();
        if (dialing) appendNumber(e.key);
        else setDtmf((v) => (v + e.key).slice(-18));
      } else if (e.key === "Backspace" && !inNumberField) {
        e.preventDefault();
        if (dialing) setNumber((n) => n.slice(0, -1));
        else setDtmf((v) => v.slice(0, -1));
      } else if (e.key === "Enter" && dialing) {
        e.preventDefault();
        document.getElementById("cw-call-btn")?.click();
      } else if (e.key === "Escape" && dtmfOpen) {
        setSheet(null);
      }
    };

    const onPaste = (e) => {
      if (!dialing || isOtherField(document.activeElement) || document.activeElement === numberRef.current) return;
      const text = e.clipboardData?.getData("text") || "";
      if (!text) return;
      e.preventDefault();
      appendNumber(text);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("paste", onPaste);
    };
  }, [dialing, dtmfOpen, flashKey, appendNumber]);

  /* ---------- pieces ---------- */

  const renderKeypad = (onKey) => (
    <div className="cw-keypad">
      {KEYS.map(([d, l]) => (
        <button
          key={d}
          type="button"
          tabIndex={-1}
          className={`cw-key ${pressed === d ? "is-pressed" : ""}`}
          onMouseDown={(e) => e.preventDefault()} // keep focus in the number field
          onClick={() => onKey(d)}
        >
          <span className="cw-key__digit">{d}</span>
          <span className="cw-key__letters">{l}</span>
        </button>
      ))}
    </div>
  );

  const renderHomeKeypad = () => (
    <div className="cw-dialer">
      <div className="cw-modes" role="tablist" aria-label="Dial mode">
        {DIAL_MODES.map((m) => (
          <button
            key={m}
            type="button"
            className={`cw-mode ${dialMode === m ? "is-active" : ""}`}
            onClick={() => setDialMode(m)}
          >
            {m}
          </button>
        ))}
      </div>

      <div className={`cw-number ${number.length > 13 ? "is-long" : ""}`}>
        <input
          ref={numberRef}
          value={number}
          onChange={(e) => setNumber(cleanNumber(e.target.value).slice(0, MAX_NUMBER))}
          placeholder="Enter number"
          inputMode="tel"
          autoComplete="off"
          aria-label="Phone number"
        />
        {number && (
          <button
            type="button"
            className="cw-backspace"
            aria-label="Delete digit"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setNumber((n) => n.slice(0, -1))}
          >
            ⌫
          </button>
        )}
      </div>

      <div className="cw-callerid">
        {number ? (
          <>
            Caller ID <b>+91 80 4567 8900</b>
          </>
        ) : (
          <>
            <kbd>0-9</kbd> type or paste · <kbd>Enter</kbd> to call
          </>
        )}
      </div>

      {renderKeypad(appendNumber)}

      <div className="cw-dial-actions">
        <button
          type="button"
          id="cw-call-btn"
          className="cw-round cw-round--call"
          aria-label="Call"
          disabled={!number || status !== "ready"}
          onClick={() => startCall()}
        >
          <PhoneFilled />
        </button>
      </div>
      {status !== "ready" && (
        <p className="cw-hint">Set your status to Ready to place calls.</p>
      )}
    </div>
  );

  const renderRecents = () => {
    const list = RECENTS.filter(
      (r) =>
        r.name.toLowerCase().includes(search.toLowerCase()) || r.number.includes(search)
    );
    return (
      <div className="cw-list-wrap">
        <label className="cw-search">
          <SearchOutlined />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or number"
          />
        </label>
        <ul className="cw-list">
          {list.map((r, i) => (
            <li key={i} className="cw-row">
              <span className={`cw-avatar cw-avatar--${r.dir}`}>{initials(r.name)}</span>
              <span className="cw-row__main">
                <b className={r.dir === "missed" ? "is-missed" : ""}>{r.name}</b>
                <small>
                  <span className={`cw-dir cw-dir--${r.dir}`}>
                    {r.dir === "in" && <ArrowDownOutlined />}
                    {r.dir === "out" && <ArrowUpOutlined />}
                    {r.dir === "missed" && <StopOutlined />}
                  </span>
                  {r.number}
                </small>
              </span>
              <span className="cw-row__meta">
                <small>{r.time}</small>
                {r.dur && <small>{r.dur}</small>}
              </span>
              <button
                type="button"
                className="cw-mini-call"
                aria-label={`Call ${r.name}`}
                disabled={status !== "ready"}
                onClick={() => startCall(r)}
              >
                <PhoneFilled />
              </button>
            </li>
          ))}
          {!list.length && <li className="cw-empty">No calls found</li>}
        </ul>
      </div>
    );
  };

  const renderCallbacks = () => (
    <div className="cw-list-wrap">
      <div className="cw-section-title">Scheduled callbacks</div>
      <ul className="cw-list">
        {CALLBACKS.map((c, i) => (
          <li key={i} className="cw-row">
            <span className="cw-avatar">{initials(c.name)}</span>
            <span className="cw-row__main">
              <b>{c.name}</b>
              <small>{c.loan} · {c.number}</small>
              <small className={`cw-due ${c.due ? "is-due" : ""}`}>
                <ClockCircleOutlined /> {c.at}
              </small>
            </span>
            <button
              type="button"
              className="cw-mini-call"
              aria-label={`Call ${c.name}`}
              disabled={status !== "ready"}
              onClick={() => startCall(c)}
            >
              <PhoneFilled />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderStatus = () => (
    <div className="cw-list-wrap">
      <div className="cw-section-title">Agent status</div>
      <ul className="cw-status-list">
        {AGENT_STATUSES.map((s) => (
          <li key={s.key}>
            <button
              type="button"
              className={`cw-status-opt cw-tone--${s.tone} ${status === s.key ? "is-active" : ""}`}
              onClick={() => setStatus(s.key)}
            >
              <span className="cw-status-opt__icon">{s.icon}</span>
              {s.label}
              {status === s.key && <CheckCircleFilled className="cw-status-opt__check" />}
            </button>
          </li>
        ))}
      </ul>
      <div className="cw-stats">
        <div><b>42</b><small>Calls today</small></div>
        <div><b>2h 18m</b><small>Talk time</small></div>
        <div><b>9</b><small>PTPs</small></div>
      </div>
    </div>
  );

  const CONTROLS = [
    { key: "mute", label: muted ? "Unmute" : "Mute", icon: muted ? <AudioMutedOutlined /> : <AudioOutlined />, active: muted, onClick: () => setMuted((v) => !v) },
    { key: "hold", label: held ? "Resume" : "Hold", icon: held ? <CaretRightOutlined /> : <PauseOutlined />, active: held, onClick: () => setHeld((v) => !v) },
    { key: "keypad", label: "Keypad", icon: <AppstoreOutlined />, active: sheet === "keypad", onClick: () => setSheet("keypad") },
    { key: "speaker", label: "Speaker", icon: <SoundOutlined />, active: speaker, onClick: () => setSpeaker((v) => !v) },
    { key: "transfer", label: "Transfer", icon: <SwapOutlined />, active: sheet === "transfer", onClick: () => setSheet("transfer") },
    { key: "conference", label: "Conference", icon: <UsergroupAddOutlined />, active: sheet === "conference", onClick: () => setSheet("conference") },
    { key: "record", label: recording ? "Recording" : "Record", icon: <span className="cw-rec-dot" />, active: recording, onClick: () => setRecording((v) => !v) },
    { key: "notes", label: "Notes", icon: <FileTextOutlined />, active: sheet === "notes", onClick: () => setSheet("notes") },
  ];

  const renderSheet = () => {
    if (!sheet) return null;
    const titles = {
      keypad: "Keypad",
      transfer: "Transfer call",
      conference: "Add to conference",
      notes: "Call notes",
    };
    return (
      <div className="cw-sheet">
        <div className="cw-sheet__head">
          <button type="button" className="cw-icon-btn" aria-label="Back" onClick={() => setSheet(null)}>
            <ArrowLeftOutlined />
          </button>
          <b>{titles[sheet]}</b>
        </div>

        {sheet === "keypad" && (
          <>
            <div className="cw-dtmf">{dtmf || " "}</div>
            {renderKeypad((d) => setDtmf((v) => (v + d).slice(-18)))}
          </>
        )}

        {(sheet === "transfer" || sheet === "conference") && (
          <>
            <div className="cw-number cw-number--sm">
              <input
                value={transferTo}
                onChange={(e) => setTransferTo(e.target.value)}
                placeholder="Number or extension"
                inputMode="tel"
              />
            </div>
            {sheet === "transfer" && (
              <div className="cw-seg">
                <button type="button" className="is-active">Warm</button>
                <button type="button">Blind</button>
              </div>
            )}
            <ul className="cw-list">
              {TRANSFER_TARGETS.map((t) => (
                <li key={t.ext} className="cw-row">
                  <span className="cw-avatar">
                    <UserOutlined />
                    <i className={`cw-presence ${t.online ? "is-on" : ""}`} />
                  </span>
                  <span className="cw-row__main">
                    <b>{t.name}</b>
                    <small>Ext. {t.ext} · {t.online ? "Available" : "Busy"}</small>
                  </span>
                  <button
                    type="button"
                    className="cw-chip-btn"
                    disabled={!t.online}
                    onClick={() => setSheet(null)}
                  >
                    {sheet === "transfer" ? "Transfer" : "Add"}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {sheet === "notes" && (
          <div className="cw-notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Type notes while you talk…"
              rows={8}
            />
            <button type="button" className="cw-primary" onClick={() => setSheet(null)}>
              Save notes
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderInCall = () => (
    <div className="cw-call">
      <div className={`cw-call__who ${screen === "calling" ? "is-ringing" : ""}`}>
        <span className="cw-avatar-ring">
          <i className="cw-ripple" />
          <i className="cw-ripple" />
          <span className="cw-avatar cw-avatar--xl">
            {contact?.name && contact.name !== "Unknown" ? initials(contact.name) : <UserOutlined />}
          </span>
        </span>
        <h3>{contact?.name}</h3>
        <p>{contact?.number}</p>
        {contact?.loan && <span className="cw-tag">{contact.loan}</span>}
        <div className={`cw-call__state ${held ? "is-held" : ""}`}>
          {screen === "calling" ? (
            <span className="cw-ringing">Calling<i>.</i><i>.</i><i>.</i></span>
          ) : held ? (
            <>On hold · {formatTimer(seconds)}</>
          ) : (
            <>{formatTimer(seconds)}</>
          )}
        </div>
        <div className="cw-call__flags">
          {recording && screen === "incall" && <span className="cw-flag cw-flag--rec">● REC</span>}
          {muted && <span className="cw-flag">Muted</span>}
          {speaker && <span className="cw-flag">Speaker</span>}
        </div>
      </div>

      <div className="cw-controls">
        {CONTROLS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`cw-control ${c.active ? "is-active" : ""} cw-control--${c.key}`}
            disabled={screen === "calling" && c.key !== "mute" && c.key !== "speaker"}
            onClick={c.onClick}
          >
            <span className="cw-control__icon">{c.icon}</span>
            <small>{c.label}</small>
          </button>
        ))}
      </div>

      <div className="cw-dial-actions">
        <button type="button" className="cw-round cw-round--end" aria-label="End call" onClick={endCall}>
          <PhoneFilled />
        </button>
      </div>

      {renderSheet()}
    </div>
  );

  const renderWrapup = () => (
    <div className="cw-wrapup">
      <div className="cw-wrapup__summary">
        <span className="cw-avatar">{contact?.name ? initials(contact.name) : "?"}</span>
        <div>
          <b>{contact?.name}</b>
          <small>{contact?.number} · Duration {formatTimer(seconds)}</small>
        </div>
      </div>

      <div className="cw-section-title">Disposition</div>
      <div className="cw-dispositions">
        {DISPOSITIONS.map((d) => (
          <button
            key={d}
            type="button"
            className={`cw-chip ${disposition === d ? "is-active" : ""}`}
            onClick={() => setDisposition(d)}
          >
            {d}
          </button>
        ))}
      </div>

      {(disposition === "Promise To Pay" || disposition === "Paid") && (
        <div className="cw-field-row">
          <label>
            Amount
            <input placeholder="₹ 0.00" inputMode="decimal" />
          </label>
          <label>
            Date
            <input type="date" />
          </label>
        </div>
      )}
      {disposition === "Call Back" && (
        <div className="cw-field-row">
          <label>
            Callback at
            <input type="datetime-local" />
          </label>
        </div>
      )}

      <label className="cw-remarks">
        Remarks
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={3}
          placeholder="Add a remark"
        />
      </label>

      <div className="cw-wrapup__actions">
        <button type="button" className="cw-secondary" onClick={finishWrapup}>
          Skip
        </button>
        <button type="button" className="cw-primary" disabled={!disposition} onClick={finishWrapup}>
          Save &amp; Next
        </button>
      </div>
    </div>
  );

  const TABS = [
    { key: "keypad", label: "Keypad", icon: <AppstoreOutlined /> },
    { key: "recents", label: "Recents", icon: <HistoryOutlined /> },
    { key: "callbacks", label: "Callbacks", icon: <ClockCircleOutlined />, badge: CALLBACKS.filter((c) => c.due).length },
    { key: "status", label: "Status", icon: <UserOutlined /> },
  ];

  const { w: panelW, h: panelH } = panelSize();

  // Same audience as the dialer iframe widget: callers only.
  if (!user?.idToken || user?.is_agency || user?.is_supervisor || pathname === "/sign-in") {
    return null;
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          className={`cw-fab ${inCall ? "is-live" : ""}`}
          style={{ left: fabPos.x, top: fabPos.y }}
          aria-label="Open call panel"
          title="Drag to move, click to open"
          onPointerDown={(e) => fabDrag.onPointerDown(e, fabPos)}
          onPointerMove={fabDrag.onPointerMove}
          onPointerUp={(e) => {
            if (!fabDrag.onPointerUp(e)) openPanel();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPanel();
            }
          }}
        >
          <PhoneFilled />
          <i className={`cw-fab__dot cw-tone--${currentStatus.tone}`} />
          {inCall && <span className="cw-fab__timer">{formatTimer(seconds)}</span>}
        </button>
      )}

      {open && (
        <div
          className="cw-panel"
          role="dialog"
          aria-label="Calling"
          style={{ left: panelPos.x, top: panelPos.y, width: panelW, height: panelH }}
        >
          <div
            className="cw-header"
            onPointerDown={(e) => {
              if (e.target.closest("button")) return;
              panelDrag.onPointerDown(e, panelPos);
            }}
            onPointerMove={panelDrag.onPointerMove}
            onPointerUp={panelDrag.onPointerUp}
          >
            <HolderOutlined className="cw-header__grip" />
            <button
              type="button"
              className={`cw-status-pill cw-tone--${currentStatus.tone}`}
              onClick={() => {
                if (screen === "home") setTab("status");
              }}
            >
              <i />
              {currentStatus.label}
            </button>
            <span className="cw-header__spacer" />
            <button type="button" className="cw-icon-btn" aria-label="Minimize" onClick={() => setOpen(false)}>
              <MinusOutlined />
            </button>
            <button
              type="button"
              className="cw-icon-btn"
              aria-label="Close"
              onClick={() => {
                if (inCall) endCall();
                setOpen(false);
              }}
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="cw-body">
            {screen === "home" && tab === "keypad" && renderHomeKeypad()}
            {screen === "home" && tab === "recents" && renderRecents()}
            {screen === "home" && tab === "callbacks" && renderCallbacks()}
            {screen === "home" && tab === "status" && renderStatus()}
            {inCall && renderInCall()}
            {screen === "wrapup" && renderWrapup()}
          </div>

          {screen === "home" && (
            <nav className="cw-tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`cw-tab ${tab === t.key ? "is-active" : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  <span className="cw-tab__icon">
                    {t.icon}
                    {!!t.badge && <em>{t.badge}</em>}
                  </span>
                  <small>{t.label}</small>
                </button>
              ))}
            </nav>
          )}
        </div>
      )}
    </>
  );
};

export default CallWidget;
